import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Save, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CategoriesTab } from "@/components/admin/categories/CategoriesTab";
import { CategoryGroupsTab } from "@/components/admin/categories/CategoryGroupsTab";
import { useCategoryGroups } from "@/hooks/useCategoryGroups";
import { useCategories } from "@/hooks/useCategories";

interface Category {
  id: string;
  name: string;
  quote_count: number;
  created_at: string;
}

export default function CategoryManager() {
  const { categories, isLoading: categoriesLoading } = useCategories();
  const { categoryGroups } = useCategoryGroups();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Create a map of categories by group for the CategoryGroupsTab
  const categoriesByGroup = categories?.reduce((acc, category) => {
        if (category.categoryGroupId) {
      if (!acc[category.categoryGroupId]) {
        acc[category.categoryGroupId] = [];
      }
      acc[category.categoryGroupId].push(category);
    }
    return acc;
  }, {} as Record<string, any[]>) || {};

  // Updated query to correctly count quotes
  const { data: categoriesData, isLoading } = useQuery({
    queryKey: ["categories"],
    queryFn: async () => {
      const { data: categoriesData, error: categoriesError } = await supabase
        .from("categories")
        .select(`
          id,
          name,
          created_at,
          quote_categories (
            quote_id
          )
        `)
        .order('name');

      if (categoriesError) throw categoriesError;

      return categoriesData.map(category => ({
        id: category.id,
        name: category.name,
        created_at: category.created_at,
        quote_count: category.quote_categories?.length || 0
      })) as Category[];
    }
  });

  // Add category mutation
  const addCategory = useMutation({
    mutationFn: async (name: string) => {
      const { data, error } = await supabase
        .from("categories")
        .insert([{ name }])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      setNewCategoryName("");
      setIsAddDialogOpen(false);
      toast({
        title: "Category added",
        description: "The category has been created successfully.",
      });
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to add category",
      });
    },
  });

  // Update category mutation
  const updateCategory = useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      const { data, error } = await supabase
        .from("categories")
        .update({ name })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      setEditingId(null);
      toast({
        title: "Category updated",
        description: "The category has been updated successfully.",
      });
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update category",
      });
    },
  });

  // Delete category mutation
  const deleteCategory = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("categories")
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      toast({
        title: "Category deleted",
        description: "The category has been deleted successfully.",
      });
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to delete category",
      });
    },
  });

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();
    if (newCategoryName.trim()) {
      addCategory.mutate(newCategoryName.trim());
    }
  };

  const handleUpdate = (id: string) => {
    if (editingName.trim()) {
      updateCategory.mutate({ id, name: editingName.trim() });
    }
  };

  const handleDelete = async (id: string, quoteCount: number) => {
    if (quoteCount > 0) {
      const confirmed = window.confirm(
        `This category contains ${quoteCount} quotes. Are you sure you want to delete it?`
      );
      if (!confirmed) return;
    }
    deleteCategory.mutate(id);
  };

  const startEditing = (category: Category) => {
    setEditingId(category.id);
    setEditingName(category.name);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold tracking-tight">Category Management</h1>
      </div>

      <Tabs defaultValue="categories">
        <TabsList>
          <TabsTrigger value="categories">Categories</TabsTrigger>
          <TabsTrigger value="groups">Category Groups</TabsTrigger>
        </TabsList>
        
        <TabsContent value="categories">
          <CategoriesTab 
            categoryGroups={categoryGroups || []} 
            sortField="name"
            sortDirection="asc"
          />
        </TabsContent>
        
        <TabsContent value="groups">
          <CategoryGroupsTab categoriesByGroup={categoriesByGroup} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
